import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import { endOfDay, formatISO, startOfDay, subDays } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import { Page, ScreenHeader } from "@APP/components";
import { NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION, commonFormatDate } from "@APP/constants";
import { AppState, hideLoader, setUserSearchResults, showLoader } from "@APP/redux";
import { filterToolbarStyles } from "@APP/styles";
import { formatErrorMessage } from "@APP/utils";
import { anonymizeUser, getSearchedUsers } from "@APP/services/api";
import { UserSearchResponse } from "@APP/types";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";

import UserList from "./UserList";

const DATE_PRESETS = ["Today", "Last 7 days", "Last 30 days", "Custom"];

const rangeFor = (label: string) => {
  const now = new Date();
  if (label === "Last 7 days") return { from: formatISO(subDays(now, 7)), to: formatISO(now) };
  if (label === "Last 30 days") return { from: formatISO(subDays(now, 30)), to: formatISO(now) };

  return { from: formatISO(startOfDay(now)), to: formatISO(now) };
};

export const UserDashboardView = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const classes = filterToolbarStyles();

  const [fromLabel, setFromLabel] = useState("Today");
  const [entries, setEntries] = useState(25);
  const [userData, setUserData] = useState<UserSearchResponse[]>([]);
  const [lastPage, setLastPage] = useState(0);
  const [submitFrom, setSubmitFrom] = useState("");
  const [submitTo, setSubmitTo] = useState("");
  const [page, setPage] = useState(0);
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState("createdDate");

  const { from, to } = useSelector(
    ({
      userAdmin: {
        rtpDashboard: { from, to },
      },
    }: AppState) => ({
      from,
      to,
    }),
  );

  const handleErrorCodes = useHandleErrorCodes();

  const fetchUsers = async ({ from, to }: { from: string; to: string }) => {
    const utcFrom = zonedTimeToUtc(startOfDay(new Date(from)), "UTC").toISOString();
    const utcTo = zonedTimeToUtc(endOfDay(new Date(to)), "UTC").toISOString();

    try {
      let sort_by = "";
      switch (orderBy) {
        case "username":
          sort_by = "username";
          break;
        case "organizationName":
          sort_by = "org_name";
          break;
        case "createdDate":
        case "createdTime":
          sort_by = "created_at";
          break;
        case "status":
          sort_by = "status";
          break;
        default:
          break;
      }
      dispatch(showLoader());
      const { data: userData, meta } = await getSearchedUsers(
        { from: utcFrom, to: utcTo },
        { page: page, entries: entries },
        `sort_by=${sort_by}&sort_type=${order}`,
      );
      if (meta.totalItems) {
        if (meta.totalItems <= entries) {
          setLastPage(1);
        } else {
          const lastPageIndex = Math.ceil(meta.totalItems / entries);
          setLastPage(lastPageIndex);
        }
      }
      dispatch(setUserSearchResults(userData));
      setUserData(userData);
    } catch (error: any) {
      const errorData = error?.resposnse?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    const dateString = new Date().toISOString();
    fetchUsers({ from: submitFrom || dateString, to: submitTo || dateString });
  }, [entries, page, order, orderBy]);

  const handleOnSubmit = ({ from, to }: { from: string; to: string }) => {
    fetchUsers({ from, to });
    setSubmitFrom(from);
    setSubmitTo(to);
    setPage(0);
  };

  const { errors, handleSubmit, touched, values, setFieldValue } = useFormik({
    initialValues: {
      from: zonedTimeToUtc(from, "UTC").toISOString(),
      to: zonedTimeToUtc(to, "UTC").toISOString(),
    },
    onSubmit: handleOnSubmit,
  });

  const handleOnEntriesChange = (entries: number) => {
    setPage(NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION);
    setEntries(entries);
  };

  useEffect(() => {
    const from = formatISO(startOfDay(new Date()));
    const to = formatISO(new Date());
    const utcFrom = zonedTimeToUtc(from, "UTC").toISOString();
    const utcTo = zonedTimeToUtc(to, "UTC").toISOString();
    fetchUsers({ from: utcFrom, to: utcTo });
  }, []);

  const handleAnonymize = async (orgId: string, customerId: string) => {
    try {
      dispatch(showLoader());
      await anonymizeUser(orgId, customerId);

      // Refresh the user list to get updated anonymisation status
      const dateString = new Date().toISOString();
      await fetchUsers({ from: submitFrom || dateString, to: submitTo || dateString });

      alert.open("Success", "Anonymisation process has been started successfully.", [
        {
          text: "Okay",
        },
      ]);
    } catch (error: any) {
      const errorData = error?.response?.data;

      // If no error code exists, show generic message
      if (!errorData?.errorCode) {
        alert.open(
          "Error",
          "Unable to start anonymisation process. Please try again or contact support if the issue persists.",
          [{ text: "Okay" }],
        );
      } else {
        // If error code exists, try to handle it
        const isHandled = handleErrorCodes(errorData.errorCode);

        if (!isHandled) {
          const errorMessage = formatErrorMessage(
            error,
            "Unable to start anonymisation process. Please try again or contact support if the issue persists.",
          );
          alert.open("Error", errorMessage, [{ text: "Okay" }]);
        }
      }
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <>
      <Page title="User Dashboard">
        <ScreenHeader title="User Dashboard" />
        <form onSubmit={handleSubmit}>
          <Box className={classes.toolbar}>
            <Box className={classes.topRow}>
              <div className={classes.segment} role="group" aria-label="Created date range">
                {DATE_PRESETS.map((label) => {
                  const isCustom = label === "Custom";

                  return (
                    <button
                      key={label}
                      type={isCustom ? "button" : "submit"}
                      aria-pressed={fromLabel === label}
                      className={`${classes.segmentButton} ${
                        fromLabel === label ? classes.segmentButtonActive : ""
                      }`}
                      onClick={() => {
                        const { from, to } = rangeFor(label);
                        setFieldValue("from", from);
                        setFieldValue("to", to);
                        setFromLabel(label);
                        setCustomDatePicker(isCustom);
                      }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Box>

            {customDatePicker && (
              <Box className={classes.customRange}>
                <DatePicker
                  label="From"
                  value={values.from}
                  inputFormat={commonFormatDate}
                  disableFuture
                  open={startDatePickerOpen}
                  onOpen={() => setStartDatePickerOpen(true)}
                  onClose={() => setStartDatePickerOpen(false)}
                  onChange={(value) => setFieldValue("from", value)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      size="small"
                      onClick={() => setStartDatePickerOpen(true)}
                      error={Boolean(touched.from && errors.from)}
                      helperText={touched.from && errors.from}
                    />
                  )}
                />
                <DatePicker
                  label="To"
                  value={values.to}
                  inputFormat={commonFormatDate}
                  disableFuture
                  open={endDatePickerOpen}
                  onOpen={() => setEndDatePickerOpen(true)}
                  onClose={() => setEndDatePickerOpen(false)}
                  onChange={(value) => setFieldValue("to", value)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      size="small"
                      onClick={() => setEndDatePickerOpen(true)}
                      error={Boolean(touched.to && errors.to)}
                      helperText={touched.to && errors.to}
                    />
                  )}
                />
                <Button type="submit" variant="contained" color="primary">
                  Apply
                </Button>
              </Box>
            )}
          </Box>
          <UserList
            data={userData}
            entries={entries}
            lastPage={lastPage}
            page={page}
            setPage={setPage}
            handleOnEntriesChange={handleOnEntriesChange}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            onAnonymize={handleAnonymize}
          />
        </form>
      </Page>
    </>
  );
};

export default UserDashboardView;
